import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import WeaAssumptions from '../WeaAssumptions';
import makeStyles from '@mui/styles/makeStyles';
import { requestTrainingMailTo } from '../../../../utils/constants';
import { styles } from './styles';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '70%',
    height: '100%',
    position: 'relative',
    margin: '1rem 10rem',
    marginBottom: '4rem',
  },
  button: {
    ...theme.palette.button,
    padding: '1rem 1.5rem !important',
  },
  requestTraining: {
    padding: '1rem 10rem',
    backgroundColor: theme.palette.baseColors.skyBlue.c25,
  },
  link: { color: '#0101d6' },
  underlined: {
    textDecoration: 'underline',
  },
  italic: {
    textDecoration: 'italic',
  },
}));

export default () => {
  const classes = useStyles();
  return (
    <>
      <div className={classes.requestTraining}>
        <h2>Request Platform Overview Session</h2>
        <p>
          Our onboarding/platform overview sessions are designed to assist you to quickly navigate
          and extract data specific to your individual requirements.
        </p>
        <Button className={classes.button} href={requestTrainingMailTo} variant="primary">
          Request training
        </Button>
        <p>We look forward to speaking with you soon.</p>
      </div>
      <div className={classes.root}>
        <div id="landing-page-documentation">
          <WeaAssumptions />
          <h1>How we define reserves and resources</h1>
          <h2>Overview</h2>
          <p>
            This document explains how we define reserves and resources at Welligence and how that
            definition compares to recognized third-party guidelines.
          </p>
          <h3>Reserves</h3>
          <p>
            “At Welligence, we model the volume of oil and gas that we estimate can be extracted and
            sold under our base-case commercial assumptions. We consider this broadly equivalent to
            a 2P reserves figure. We may also treat contingent resources up to 2C as commercial
            e.g., a promising new discovery or unconventional resources that may be developed over a
            long-time horizon. Note that it is the liquids and gas available for sale to the market
            that we classify as reserves – volumes such as gas used in-field as fuel are excluded.
            We also model assets to their economic limit, rather than contract expiry.”
          </p>
          <h3>Sub-commercial Resources</h3>
          <p>
            “These are the volumes of oil and gas that we believe are potentially recoverable from a
            discovered accumulation but are not currently considered commercial due to one or more
            reasons. For example, a stranded gas field with no market, or a new discovery that needs
            appraisal. A producing field can also hold sub-commercial resource – this could be a
            deep gas interval, or an area with poor reservoir quality.”
          </p>
          <h2>Petroleum Resources Management System – definitions</h2>
          <p>
            The{' '}
            <a
              href="https://www.spe.org/en/industry/petroleum-resources-management-system-2018/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Petroleum Resources Management System
            </a>{' '}
            (PRMS) is the guidelines collated by several professional institutions, including the
            SPE and AAPG. We use these definitions as the basis for our reserve and resource
            classifications.
          </p>
          <h3>STOIIP – Stock Tank Oil Initially In Place</h3>
          <p>
            The volume of oil estimated to be contained within known accumulations before the start
            of production.
          </p>
          <ul>
            <li>
              Also referred to as
              <span className={classes.italic}>Stock Tank Oil Originally in Place (STOOIP)</span>
              and <span className={classes.italic}>Oil Initially In Place (OIIP)</span>
            </li>
            <li>
              The gas equivalent is
              <span className={classes.italic}>Gas Initially In Place (GIIP)</span>
            </li>
          </ul>
          <h3>Reserves</h3>
          <p>
            The volume of oil/gas estimated to be commercially recoverable from known accumulations
            under a defined set of base-case conditions.
            <ul>
              <li>We only include sales quantities as reserves (see next page)</li>
            </ul>
          </p>
          <h3>Contingent Resources</h3>
          <p>
            The volume of oil/gas estimated to be potentially recoverable from known accumulations
            but not currently considered commercial due to one or more contingencies.
          </p>

          <h2>Why do we only classify sales volumes as reserves?</h2>
          <p>
            We count the oil and gas available for sale to the market as reserves, excluding all
            other hydrocarbons produced e.g., flaring, in-field fuel use, etc.
          </p>

          <span className={classes.underlined}>The following volumes are excluded</span>
          <ul>
            <li>
              Gas flared
              <span className={classes.italic}>Gas flared</span>
            </li>
            <li>
              <span className={classes.italic}>Gas vented</span>
            </li>
            <li>
              <span className={classes.italic}>Gas re-injected for disposal/pressure support</span>
            </li>
            <li>
              <span className={classes.italic}>Oil & gas used in-field for fuel</span>
            </li>
            <li>
              <span className={classes.italic}>Impurities and other components</span>
            </li>
          </ul>

          <span className={classes.underlined}>Why do we exclude these volumes?</span>
          <p>
            Our reserves must have a direct monetary value i.e., generate revenue. Some items like
            gas flared/vented or injected for disposal reasons, while being operational necessities,
            provide no direct revenue. Other items do bring value to operations, but this is
            captured indirectly through non-revenue mechanisms.
          </p>
          <ul>
            <li>
              <span className={classes.italic}>
                Oil & gas used in-field for fuel: The value is reflected in lower operating costs as
                gas/diesel does not need to be purchased
              </span>
            </li>
            <li>
              <span className={classes.italic}>
                Gas re-injected for pressure support: The value is reflected in higher oil recovery
              </span>
            </li>
          </ul>

          <h2>The Welligence reserve categories – PDP vs. UD</h2>
          <p>
            For any commercial asset on our platform, our reserves can be split into two categories
            – Proved Developed Producing (PDP) and Undeveloped (UD).
          </p>

          <span className={classes.underlined}>Proved Developed Producing</span>
          <p>
            This is production from wells already drilled and online. With the wells and associated
            facilities already in place, no further investment is required.
          </p>
          <ul>
            <li>
              <span className={classes.italic}>
                For those countries with well-level data, our algorithms decline out production from
                each well
              </span>
            </li>
            <li>
              <span className={classes.italic}>
                The approach we take for other countries depends on the data available, from
                applying a decline curve algorithm at the field level to implementing a manual
                forecast (e.g. a gas field that produces a fixed volume dictated by a sales
                contract)
              </span>
            </li>
          </ul>

          <p className={classes.italic}>
            Without any capex requirement, this production is typically low cost and remains online
            in a low oil price scenario.
          </p>

          <span className={classes.underlined}>Undeveloped</span>
          <p>
            This is production from new wells that we expect to be drilled at the asset. Capex is
            required, from drilling to (possibly) installing infrastructure like facilities,
            pipelines, etc.
          </p>
          <ul>
            <li>
              <span className={classes.italic}>
                We use a production curve to forecast production from each new well
              </span>
            </li>
            <li>
              <span className={classes.italic}>
                For those countries with well-level data, our algorithm calculates a production
                curve
              </span>
            </li>
            <li>
              <span className={classes.italic}>
                For the others, we select production curves based on analogues, primary research,
                operator guidance, etc.
              </span>
            </li>
          </ul>
          <span>
            This is higher cost production and is at risk in a low oil price scenario as companies
            defer investment.{' '}
          </span>

          <TableContainer component={Paper} sx={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.tableHeader}>WEA</TableCell>
                  <TableCell sx={styles.tableHeader}>PRMS</TableCell>
                  <TableCell sx={styles.tableHeader}>PRMS Definition</TableCell>
                  <TableCell sx={styles.tableHeader}>Comment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell rowSpan={2}>Proved Developed Producing (PDP)</TableCell>
                  <TableCell>2PDP (Proved Developed Producing)</TableCell>
                  <TableCell>
                    P50 Reserves (Proved) expected to be recovered from existing wells and
                    facilities that are already producing.
                  </TableCell>
                  <TableCell>
                    Based on actual production performance. Lowest risk category and reflects
                    immediate cash flow.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>2PNDP (Proved Developed Non-Producing)</TableCell>
                  <TableCell>
                    P50 Reserves (Proved) expected to be recovered from existing wells and
                    facilities that are already producing.
                  </TableCell>
                  <TableCell>
                    Requires only minor operations (workovers, recompletions). Timing/cost risk is
                    limited.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell rowSpan={2}>Undeveloped (UD)</TableCell>
                  <TableCell>2PUD (Proved + Probable Undeveloped)</TableCell>
                  <TableCell>
                    P50 Reserves (Proved + Probable) from known accumulations that require new wells
                    or facilities; project is commercial and planned.
                  </TableCell>
                  <TableCell>
                    Requires new drilling or major capex (infill drilling, tiebacks). Development
                    plan must be approved and economic under current conditions.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>2C (Best Estimate Contingent Resources)</TableCell>
                  <TableCell>
                    P50 Resources (Best Estimate Contingent) from known accumulations that are
                    discovered but not yet commercial due to contingencies (economic, technical,
                    regulatory, or project maturity).
                  </TableCell>
                  <TableCell>
                    Future projects and upside within line of sight; discovered but not yet
                    commercialized. Volumes expected to be developed as plans mature, or
                    contingencies are resolved.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Modeled Reserves = PDP + UD</TableCell>
                  <TableCell>Modeled Reserves = 2PDP + 2PDNP + 2PUD + 2C</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <h2>The Welligence reserves categories – by development status</h2>

          <TableContainer component={Paper} sx={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.tableHeader}>Project Development Status</TableCell>
                  <TableCell sx={styles.tableHeader}>Proved Developed Producing (PDP)</TableCell>
                  <TableCell sx={styles.tableHeader}>Reserves Status Undeveloped (UD)</TableCell>
                  <TableCell sx={styles.tableHeader}>Comments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Sub-Commercial</TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CloseIcon />
                  </TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CloseIcon />
                  </TableCell>
                  <TableCell>The resource is 2C</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Pre-sanction (to be appraised)</TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CloseIcon />
                  </TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CheckIcon />
                  </TableCell>
                  <TableCell>The resource is 2C</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Pre-sanction (appraised but pre-FEED)</TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CloseIcon />
                  </TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CheckIcon />
                  </TableCell>
                  <TableCell>The resource is 2C</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Pre-sanction (FEED underway)</TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CloseIcon />
                  </TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CheckIcon />
                  </TableCell>
                  <TableCell>The resource is 2C</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Pre-sanction (FEED underway)</TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CloseIcon />
                  </TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CheckIcon />
                  </TableCell>
                  <TableCell>The resource is 2C</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Under Development</TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CloseIcon />
                  </TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CheckIcon />
                  </TableCell>
                  <TableCell>The resource is 2P, and possibly some 2C</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Onstream</TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CheckIcon />
                  </TableCell>
                  <TableCell sx={styles.tableIcon}>
                    <CheckIcon />
                  </TableCell>
                  <TableCell>The resource is 2P, and possibly some 2C</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </>
  );
};
